import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  buildHotelRoomGroups,
  computeHotelMetrics,
  computeEventHotelMetrics,
  computeHotelPricing,
  computeEventHotelPricing,
  formatHotelAddress,
  formatRoomPrice,
  getBlockCategoryBaseTitle,
  normalizeNights,
} from "../utils/hotelRoomUtils";
import "../assets/css/HotelRoomPreview.css";

const OccupancyBar = ({ occupied, available, total }) => {
  const occupiedPct = total ? Math.round((occupied / total) * 100) : 0;
  const availablePct = total ? Math.max(100 - occupiedPct, 0) : 0;
  return (
    <div
      className="hotel-preview-bar"
      role="img"
      aria-label={`${occupied} taken, ${available} available of ${total} rooms`}
      title={`${occupied} taken · ${available} available`}
    >
      <span
        className="hotel-preview-bar-occupied"
        style={{ width: `${occupiedPct}%` }}
      />
      <span
        className="hotel-preview-bar-available"
        style={{ width: `${availablePct}%` }}
      />
    </div>
  );
};

const RoomTypeGroups = ({ groups, idPrefix = "room" }) => {
  if (!groups.length) {
    return (
      <div className="hotel-preview-empty hotel-preview-empty-inline">
        No rooms configured for this hotel.
      </div>
    );
  }

  return (
    <div className="hotel-preview-room-type-groups">
      {groups.map((group) => {
        const baseTitle =
          group.baseTitle ||
          getBlockCategoryBaseTitle(group.roomType, group.bedType);
        const priceLabel =
          group.priceLabel || formatRoomPrice(group.currency, group.price);
        return (
          <div
            className="hotel-preview-room-type-group"
            key={`${idPrefix}-${group.key}`}
          >
            <div className="hotel-preview-room-type-label">
              <span className="hotel-preview-room-type-name">{baseTitle}</span>
              <div className="hotel-preview-room-type-meta">
                {priceLabel ? (
                  <span
                    className="hotel-preview-room-type-price"
                    title={`${priceLabel} per night`}
                  >
                    {priceLabel}
                    <em>/night</em>
                  </span>
                ) : null}
                <em className="hotel-preview-room-type-counts">
                  {group.availableCount} free · {group.occupiedCount} taken ·{" "}
                  {group.items.length}
                </em>
              </div>
            </div>
            <div className="hotel-preview-room-pills">
              {group.items.map((room) => {
                const isOccupied = room.status === "occupied";
                return (
                  <span
                    key={`${idPrefix}-${group.key}-${room.roomNumber}`}
                    className={`hotel-preview-room-pill is-${
                      isOccupied ? "occupied" : "available"
                    }`}
                    title={
                      isOccupied
                        ? room.guestName
                          ? `${room.roomNumber} · ${room.guestName}`
                          : `Room ${room.roomNumber} — assigned`
                        : `Room ${room.roomNumber} — available`
                    }
                  >
                    {room.roomNumber}
                  </span>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const HotelInventoryCard = ({
  hotel,
  accentIndex = 0,
  compact = false,
  showPricing = true,
}) => {
  const groups = useMemo(
    () => buildHotelRoomGroups(hotel.rooms || []),
    [hotel.rooms]
  );
  const metrics = useMemo(() => {
    if (hotel.metrics?.totalRooms != null) {
      return {
        totalRooms: hotel.metrics.totalRooms,
        occupied: hotel.metrics.occupied ?? 0,
        available:
          hotel.metrics.available ??
          Math.max(
            (hotel.metrics.totalRooms ?? 0) - (hotel.metrics.occupied ?? 0),
            0
          ),
      };
    }
    return computeHotelMetrics(hotel.rooms || []);
  }, [hotel.metrics, hotel.rooms]);

  const pricing = useMemo(
    () =>
      computeHotelPricing(
        hotel.rooms || [],
        hotel.totalNights != null ? hotel.totalNights : 1
      ),
    [hotel.rooms, hotel.totalNights]
  );

  const addressLine = formatHotelAddress(hotel.address);
  const hotelKey = hotel._id || hotel.hotelName;
  const accentClass = `accent-${(accentIndex % 5) + 1}`;

  return (
    <article
      className={`hotel-preview-card ${accentClass}${
        compact ? " is-compact" : ""
      }`}
      id={`hotel-preview-${hotelKey}`}
    >
      <div className="hotel-preview-card-head">
        <div className="hotel-preview-card-title-wrap">
          <h3 className="hotel-preview-card-title">
            {hotel.hotelName || "Hotel"}
          </h3>
          {addressLine ? (
            <p className="hotel-preview-card-address">{addressLine}</p>
          ) : null}
          <p className="hotel-preview-card-summary">
            {metrics.occupied} taken · {metrics.available} available ·{" "}
            {metrics.totalRooms} total · {pricing.nights}{" "}
            {pricing.nights === 1 ? "night" : "nights"}
          </p>
        </div>
        <div className="hotel-preview-card-stats">
          <span className="hotel-preview-card-stat">
            <em>Total</em>
            <strong>{metrics.totalRooms}</strong>
          </span>
          <span className="hotel-preview-card-stat is-nights">
            <em>Nights</em>
            <strong>{pricing.nights}</strong>
          </span>
          <span className="hotel-preview-card-stat is-available">
            <em>Avail</em>
            <strong>{metrics.available}</strong>
          </span>
          <span className="hotel-preview-card-stat is-occupied">
            <em>Taken</em>
            <strong>{metrics.occupied}</strong>
          </span>
        </div>
      </div>

      <div className="hotel-preview-card-body">
        {showPricing && pricing.label ? (
          <div
            className="hotel-preview-card-pricing"
            title={`${pricing.breakdown} (room rate × nights)`}
          >
            <em>
              Pricing
              {pricing.nights
                ? ` · ${pricing.nights} ${
                    pricing.nights === 1 ? "night" : "nights"
                  }`
                : ""}
            </em>
            <strong>{pricing.label}</strong>
          </div>
        ) : null}
        <OccupancyBar
          occupied={metrics.occupied}
          available={metrics.available}
          total={metrics.totalRooms}
        />

        <RoomTypeGroups groups={groups} idPrefix={String(hotelKey)} />
      </div>
    </article>
  );
};

const HotelJumpMenu = ({ hotels, onJump }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (event) => {
      if (!wrapRef.current?.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const matches = hotels.filter((hotel) =>
    String(hotel.hotelName || "")
      .toLowerCase()
      .includes(query.trim().toLowerCase())
  );

  return (
    <div className="hotel-preview-jump" ref={wrapRef}>
      <button
        type="button"
        className={`hotel-preview-jump-btn${open ? " is-open" : ""}`}
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <i className="bi bi-building" aria-hidden="true" />
        <span>Jump to hotel</span>
        <i
          className={`bi bi-chevron-${open ? "up" : "down"}`}
          aria-hidden="true"
        />
      </button>
      {open ? (
        <div className="hotel-preview-jump-menu" role="listbox">
          <input
            type="text"
            autoFocus
            className="form-control hotel-preview-jump-search"
            placeholder="Search hotel..."
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            aria-label="Search hotels"
          />
          <ul className="hotel-preview-jump-list">
            {matches.length === 0 ? (
              <li className="hotel-preview-jump-empty">No hotels match</li>
            ) : (
              matches.map((hotel) => {
                const hotelKey = hotel._id || hotel.hotelName;
                const originalIndex = Math.max(
                  hotels.findIndex(
                    (item) => (item._id || item.hotelName) === hotelKey
                  ),
                  0
                );
                const metrics =
                  hotel.metrics || computeHotelMetrics(hotel.rooms || []);
                const available =
                  metrics.available ??
                  Math.max(
                    (metrics.totalRooms ?? 0) - (metrics.occupied ?? 0),
                    0
                  );
                return (
                  <li key={hotelKey}>
                    <button
                      type="button"
                      className={`hotel-preview-jump-item accent-${
                        (originalIndex % 5) + 1
                      }`}
                      onClick={() => {
                        onJump(hotelKey);
                        setOpen(false);
                        setQuery("");
                      }}
                    >
                      <span className="hotel-preview-jump-item-name">
                        {hotel.hotelName}
                      </span>
                      <span className="hotel-preview-jump-item-counts">
                        <strong>{available}</strong> free
                        <em>{metrics.totalRooms ?? 0} total</em>
                      </span>
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

const HotelRoomPreviewModal = ({
  isOpen,
  onClose,
  viewMode = "single",
  eventName = "",
  hotels = [],
  hotelName,
  address,
  rooms = [],
  metrics,
  totalNights,
  isDraft = false,
}) => {
  const isEventView = viewMode === "event";
  const resolvedNights = normalizeNights(totalNights);

  const singleHotel = useMemo(() => {
    if (isEventView) return null;
    return {
      _id: "single",
      hotelName: hotelName || "Hotel",
      address,
      rooms: rooms || [],
      metrics,
      totalNights: resolvedNights,
    };
  }, [isEventView, hotelName, address, rooms, metrics, resolvedNights]);

  const singleSummary = useMemo(() => {
    if (isEventView) return null;
    if (metrics?.totalRooms != null) {
      return {
        totalRooms: metrics.totalRooms,
        occupied: metrics.occupied ?? 0,
        available:
          metrics.available ??
          Math.max((metrics.totalRooms ?? 0) - (metrics.occupied ?? 0), 0),
      };
    }
    return computeHotelMetrics(rooms);
  }, [isEventView, metrics, rooms]);

  const singlePricing = useMemo(
    () =>
      isEventView ? null : computeHotelPricing(rooms || [], resolvedNights),
    [isEventView, rooms, resolvedNights]
  );

  const eventSummary = useMemo(
    () => (isEventView ? computeEventHotelMetrics(hotels) : null),
    [isEventView, hotels]
  );

  const eventPricing = useMemo(
    () => (isEventView ? computeEventHotelPricing(hotels) : null),
    [isEventView, hotels]
  );

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const addressLine = formatHotelAddress(address);
  const title = isEventView
    ? eventName
      ? `${eventName} — Hotels`
      : "All Hotels Overview"
    : hotelName || "Hotel";

  const scrollToHotel = (hotelId) => {
    window.requestAnimationFrame(() => {
      document
        .getElementById(`hotel-preview-${hotelId}`)
        ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
  };

  return (
    <div
      className="hotel-preview-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="hotel-preview-title"
      onClick={onClose}
    >
      <div
        className={`hotel-preview-dialog is-compact-view${
          isEventView ? " is-event-view" : ""
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="hotel-preview-header">
          <div className="hotel-preview-header-row">
            <div className="hotel-preview-header-title-wrap">
              <h2 className="hotel-preview-title" id="hotel-preview-title">
                {title}
              </h2>
              {!isEventView && addressLine ? (
                <p className="hotel-preview-address">{addressLine}</p>
              ) : null}
            </div>
            <div
              className={`hotel-preview-stats${
                isEventView ? " is-event-stats" : ""
              }`}
            >
              {isEventView ? (
                <>
                  <div className="hotel-preview-stat">
                    <em>Hotels</em>
                    <strong>{eventSummary.hotels}</strong>
                  </div>
                  <div className="hotel-preview-stat">
                    <em>Total</em>
                    <strong>{eventSummary.totalRooms}</strong>
                  </div>
                  <div className="hotel-preview-stat is-available">
                    <em>Available</em>
                    <strong>{eventSummary.available}</strong>
                  </div>
                  <div className="hotel-preview-stat is-occupied">
                    <em>Taken</em>
                    <strong>{eventSummary.occupied}</strong>
                  </div>
                  {eventPricing?.label ? (
                    <div
                      className="hotel-preview-stat is-pricing"
                      title="Sum of each hotel’s rooms × its nights"
                    >
                      <em>Pricing</em>
                      <strong>{eventPricing.label}</strong>
                    </div>
                  ) : null}
                </>
              ) : (
                <>
                  <div className="hotel-preview-stat">
                    <em>Total</em>
                    <strong>{singleSummary.totalRooms}</strong>
                  </div>
                  <div className="hotel-preview-stat">
                    <em>Nights</em>
                    <strong>{resolvedNights}</strong>
                  </div>
                  <div className="hotel-preview-stat is-available">
                    <em>Available</em>
                    <strong>{singleSummary.available}</strong>
                  </div>
                  <div className="hotel-preview-stat is-occupied">
                    <em>Taken</em>
                    <strong>{singleSummary.occupied}</strong>
                  </div>
                  {singlePricing?.label ? (
                    <div
                      className="hotel-preview-stat is-pricing"
                      title={singlePricing.breakdown}
                    >
                      <em>Pricing</em>
                      <strong>{singlePricing.label}</strong>
                    </div>
                  ) : null}
                </>
              )}
            </div>
            <button
              type="button"
              className="hotel-preview-close"
              onClick={onClose}
              aria-label="Close preview"
            >
              ×
            </button>
          </div>
        </header>

        <div className="hotel-preview-toolbar">
          <div className="hotel-preview-legend">
            <span className="hotel-preview-legend-item">
              <span className="hotel-preview-legend-dot is-available" />
              Available
            </span>
            <span className="hotel-preview-legend-item">
              <span className="hotel-preview-legend-dot is-occupied" />
              Taken
            </span>
          </div>
          {isEventView && hotels.length > 3 ? (
            <HotelJumpMenu hotels={hotels} onJump={scrollToHotel} />
          ) : null}
          {!isEventView && isDraft ? (
            <span className="hotel-preview-draft-note">Unsaved inventory</span>
          ) : null}
        </div>

        <div className="hotel-preview-body">
          {isEventView ? (
            hotels.length === 0 ? (
              <div className="hotel-preview-empty">
                No hotels configured for this event yet.
              </div>
            ) : (
              <div className="hotel-preview-card-grid">
                {hotels.map((hotel, index) => (
                  <HotelInventoryCard
                    key={hotel._id || hotel.hotelName}
                    hotel={hotel}
                    accentIndex={index}
                    compact
                  />
                ))}
              </div>
            )
          ) : !singleHotel || !(rooms || []).length ? (
            <div className="hotel-preview-empty">
              No rooms to display. Add room blocks to see the hotel layout.
            </div>
          ) : (
            <div className="hotel-preview-card-grid">
              <HotelInventoryCard
                hotel={singleHotel}
                compact
                showPricing={false}
              />
            </div>
          )}
        </div>

        <footer className="hotel-preview-footer">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={onClose}
          >
            Close
          </button>
        </footer>
      </div>
    </div>
  );
};

export default HotelRoomPreviewModal;
